/**
 * Shared Tailwind class strings for consistent form, filter, table, and list UI.
 * Reference: vehicle / vehicle-owner modules (rounded-xl design system).
 */

export const formInputClass = (hasError = false) =>
  `w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${
    hasError ? 'border-red-400' : 'border-gray-300'
  }`;

export const formTextareaClass = (hasError = false) => formInputClass(hasError);

export const formSelectClass = (hasError = false) =>
  `w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base font-medium text-gray-700 bg-white ${
    hasError ? 'border-red-400' : 'border-gray-300'
  }`;

/** Shared size for every filter control (search, select, date, trigger, button). */
export const filterControlClass =
  'w-full h-[42px] min-h-[42px] box-border px-3 py-0 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm leading-[42px] text-gray-800 bg-white placeholder:text-sm placeholder:text-gray-500';

export const filterLabelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

export const filterInputClass = filterControlClass;

/** Date inputs — same height and type size as other filter fields */
export const filterDateInputClass =
  'w-full max-w-full min-w-0 h-[42px] min-h-[42px] box-border px-3 py-0 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm leading-[42px] date-input';

/** From/To date pair — two columns on phone and up */
export const filterDatePairClass =
  'grid grid-cols-2 gap-2 sm:gap-3 w-full min-w-0 sm:w-auto';

export const filterDateFieldClass = 'min-w-0 w-full sm:w-44';

/** Excel/PDF pair — two columns on phone, inline from lg */
export const filterExportPairClass =
  'grid grid-cols-2 gap-2 w-full min-w-0 lg:flex lg:w-auto lg:items-center';

/** Excel/PDF next to date fields — same height as filterDateInputClass */
export const filterExportButtonClass =
  '!h-[42px] !min-h-[42px] !py-0 px-3 text-sm rounded-xl w-full lg:w-auto justify-center';

export const filterSelectClass =
  'w-full h-[42px] min-h-[42px] box-border px-3 py-0 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium leading-[42px] text-gray-700 bg-white overflow-hidden';

export const filterCheckboxOptionClass =
  'flex flex-row items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm leading-5 text-gray-800';

/** Forces every field inside ListFilterPanel to the same size even if a page passed custom classes. */
export const filterFieldsNormalizeClass = [
  '[&>div]:relative [&>div]:min-w-0',
  '[&>div>label]:!block [&>div>label]:!text-sm [&>div>label]:!font-medium [&>div>label]:!leading-5 [&>div>label]:!mb-1.5 [&>div>label]:!text-gray-700',
  '[&_label.flex]:!flex [&_label.flex]:!flex-row [&_label.flex]:!items-center [&_label.flex]:!gap-2 [&_label.flex]:!mb-0 [&_label.flex]:!leading-5',
  '[&_input[type=checkbox]]:!h-4 [&_input[type=checkbox]]:!w-4 [&_input[type=checkbox]]:!min-h-0 [&_input[type=checkbox]]:!min-w-0 [&_input[type=checkbox]]:!max-h-4 [&_input[type=checkbox]]:!p-0 [&_input[type=checkbox]]:!m-0',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!h-[42px]',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!min-h-[42px]',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!py-0',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!px-3',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!text-sm',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!leading-[42px]',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!box-border',
  '[&_input:not([type=checkbox]):not([type=radio]):not([role=combobox])]:!text-gray-800',
  '[&_input[role=combobox]]:!h-[42px] [&_input[role=combobox]]:!min-h-[42px]',
  '[&_input[role=combobox]]:!py-0 [&_input[role=combobox]]:!pl-10 [&_input[role=combobox]]:!pr-10',
  '[&_input[role=combobox]]:!text-sm [&_input[role=combobox]]:!leading-[42px]',
  '[&_input[role=combobox]]:!box-border [&_input[role=combobox]]:!text-gray-800',
  '[&_select]:!h-[42px] [&_select]:!min-h-[42px] [&_select]:!py-0 [&_select]:!px-3 [&_select]:!text-sm [&_select]:!leading-[42px] [&_select]:!box-border',
  '[&_button[aria-haspopup=listbox]]:!h-[42px] [&_button[aria-haspopup=listbox]]:!min-h-[42px] [&_button[aria-haspopup=listbox]]:!text-sm [&_button[aria-haspopup=listbox]]:!px-3 [&_button[aria-haspopup=listbox]]:!py-0 [&_button[aria-haspopup=listbox]]:!leading-[42px]',
  '[&>div>.cursor-pointer]:!min-h-[42px] [&>div>.cursor-pointer]:!h-[42px] [&>div>.cursor-pointer]:!text-sm [&>div>.cursor-pointer]:!px-3 [&>div>.cursor-pointer]:!leading-[42px]',
  '[&>div>div.flex]:!h-[42px] [&>div>div.flex]:!min-h-[42px] [&>div>div.flex]:!text-sm',
  '[&>div>div.flex>button]:!h-full [&>div>div.flex>button]:!min-h-0 [&>div>div.flex>button]:!text-sm [&>div>div.flex>button]:!px-2 [&>div>div.flex>button]:!py-0',
  '[&_[role=listbox]_button]:!h-auto [&_[role=listbox]_button]:!min-h-0 [&_[role=listbox]_button]:!max-h-none [&_[role=listbox]_button]:!py-2 [&_[role=listbox]_button]:!leading-snug',
].join(' ');

export const selectDropdownPanelClass =
  'absolute left-0 right-0 top-full z-[200] mt-1 w-full min-w-0 max-w-full bg-white border border-gray-300 rounded-xl shadow-xl max-h-72 overflow-y-auto overscroll-contain py-1';

export const selectDropdownPanelDropUpClass =
  'absolute left-0 right-0 bottom-full z-[200] mb-1 w-full min-w-0 max-w-full bg-white border border-gray-300 rounded-xl shadow-xl max-h-72 overflow-y-auto overscroll-contain py-1';

export const selectDropdownPanelPortalClass =
  'fixed z-[400] min-w-0 bg-white border border-gray-300 rounded-xl shadow-xl overflow-y-auto overscroll-contain py-1';

export const selectDropdownOptionClass = (selected = false) =>
  `w-full text-left px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base leading-snug break-words whitespace-normal transition-colors ${
    selected ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700 hover:bg-blue-50/70'
  }`;

/** Open / focused dropdown trigger (SelectField, multi-select) */
export const selectDropdownOpenClass = 'border-blue-400 ring-2 ring-blue-400';

export const formCardClass = 'bg-white rounded-xl shadow-sm p-4 sm:p-8';

export const formLabelClass = 'block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2';

export const detailCardClass = 'bg-gray-50 rounded-xl p-4 border border-gray-200';

export const filterGridClassName =
  'grid grid-cols-1 min-[480px]:grid-cols-2 gap-2 md:grid-cols-3 md:gap-3 xl:grid-cols-4 2xl:grid-cols-5 [&>*]:min-w-0';

export const filterPanelClass = 'bg-white p-4 sm:p-6 rounded-xl shadow-sm mb-4 sm:mb-6';

export const listPageWrapperClass = 'w-full min-w-0 max-w-full mx-auto';

/** Clickable ID / primary redirect link in list tables & cards — matches Shipment ID */
export const listIdLinkClass =
  'font-regular text-secondary hover:text-secondary hover:underline cursor-pointer';

export const listIdLinkMobileClass =
  'font-regular text-secondary hover:text-secondary hover:underline cursor-pointer text-sm break-all';

/** Scope / filter pills (User Management, Shipment network tabs, etc.) */
export const listScopeTabActiveClass = 'bg-buttonBg text-white border-buttonBg';

export const listScopeTabInactiveClass =
  'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400';

export const listScopeTabClass = (active = false) =>
  `px-3 py-1.5 rounded-xl text-sm font-medium border transition-colors ${
    active ? listScopeTabActiveClass : listScopeTabInactiveClass
  }`;

export const listScopeTabSmClass = (active = false) =>
  `px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
    active ? listScopeTabActiveClass : listScopeTabInactiveClass
  }`;

export const tableContainerClass = 'hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0';

export const tableClass = 'w-full border-collapse';

export const tableHeadRowClass = 'bg-gray-50 text-gray-700 border-b';

export const tableHeadCellClass =
  'px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap';

export const tableBodyClass = 'divide-y divide-gray-200';

export const tableRowClass = 'hover:bg-gray-50 transition-colors';

export const tableCellClass = 'px-4 py-3 whitespace-nowrap';

export const mobileCardClass = 'bg-white rounded-xl shadow p-4 sm:p-5';

export const paginationFooterClass =
  'bg-gray-50 px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t-2 border-gray-200';

/** Settlement / review dialogs: full-width on phone, wide on iPad + desktop */
export const settlementModalOverlayClass =
  'fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 p-0 pt-14 sm:p-4 sm:pt-[4.5rem] md:p-6 md:pt-[4.5rem]';

/** Height follows content; cap below the app header so the panel never covers it */
export const settlementModalPanelClass =
  'bg-white w-full sm:rounded-2xl shadow-xl max-w-lg md:max-w-4xl lg:max-w-5xl xl:max-w-6xl max-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-6.5rem)] flex flex-col rounded-t-2xl overflow-hidden';

/** Wide review (batch tables): near-full width on phone/iPad, extra wide on desktop */
export const settlementModalPanelWideClass =
  'bg-white w-full sm:rounded-2xl shadow-xl sm:max-w-[calc(100vw-1.5rem)] md:max-w-5xl lg:max-w-6xl xl:max-w-7xl max-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-6.5rem)] md:h-[calc(100dvh-6.5rem)] flex flex-col rounded-t-2xl overflow-hidden';

/** Compact view dialogs (commission / agent detail) hug content — no wide empty columns */
export const settlementModalPanelCompactClass =
  'bg-white w-full sm:rounded-2xl shadow-xl max-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-6.5rem)] flex flex-col rounded-t-2xl overflow-hidden';

export const settlementModalHeaderClass =
  'flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 py-2 sm:py-3 border-b border-sky-100 bg-sky-50/80 shrink-0';

export const settlementModalBodyClass =
  'flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-5 py-2.5 sm:py-3';

export const settlementModalBodyCompactClass =
  'overflow-hidden px-4 py-3';

/** Review/view: body does not scroll on md+ — columns handle overflow */
export const settlementModalBodyFillClass =
  'flex-1 min-h-0 overflow-y-auto md:overflow-hidden overscroll-contain px-3 sm:px-5 py-2.5 sm:py-3';

export const settlementModalGridClass =
  'grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 items-start';

/** Human review / settlement view: details+actions left, AI review right (stack on phone) */
export const settlementReviewLayoutClass =
  'grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 items-stretch md:h-full md:min-h-0 text-sm';

export const settlementReviewMainClass =
  'min-w-0 flex flex-col gap-2 md:min-h-0 md:overflow-hidden';

export const settlementReviewMainScrollClass =
  'flex flex-col gap-2 min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain';

export const settlementReviewActionsClass = 'flex gap-2 shrink-0 pt-0.5';

export const settlementReviewTileClass = 'min-w-0 py-0.5';

/** Right column sizes to the left; AI panel fills it and scrolls internally if needed */
export const settlementReviewAiSlotClass = 'min-w-0 md:relative md:min-h-0 md:h-full';

export const settlementReviewAiPanelClass =
  'flex flex-col rounded-xl border border-gray-200 border-l-4 border-l-orange-400 bg-white p-3 min-h-0 md:absolute md:inset-0 md:overflow-hidden';

export const settlementReviewAiScrollClass =
  'min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-2';

export const emptyStateClass = 'bg-white p-8 rounded-xl shadow-sm text-center text-gray-600';

export const addButtonClass =
  'group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap';
